module Illy.SafeStack.Client.DatePicker

open Illy.SafeStack.Client.Shared
open Fable.Core
open Fable.Core.JsInterop
open Elmish
open Fable.React
open Fable.React.Props
open System
open Elmish.React
open Fable.Import

let [<Literal>] MAX_POPUP_HEIGHT = 303.

type Model =
    {
        Id: string
        Value: ParsedDate
        Month: int
        Year: int
        Anchor: DateTime
        ShowingCalendar: bool
        CalendarPosition: HTMLElementPosition.Position option
        AllowFuture: bool
        FirstDayOfWeek: DayOfWeek
        ValidationError: string option
        ValidationWarning: string option
        Label: string
        Placeholder: string option
        IsMandatory: bool
        Disabled: bool
    }

type TextInputId = TextInputId of string

type Msg =
    | DayClicked of DateTime
    | DecrementMonth
    | IncrementMonth
    | ViewMonth of int
    | ViewYear of int
    | ShowCalendar of TextInputId
    | ToggleCalendar of TextInputId
    | HideCalendar
    | SelectToday
    | PositionCalendar of TextInputId
    | SetValue of ParsedDate * string option

let recalcDateWindow model =
    let a = DateTime(model.Year, model.Month, 1)
    {
        model with
            FirstDayOfWeek = a.DayOfWeek
            Anchor = a
    }

let initWithDisabled id label placeholder mandatory allowFutureDates value disabled = 
    let t = Option.defaultValue DateTime.Today value
    {
        Id = id
        Value = ParsedDate.fromOption value
        Month = t.Month
        Year = t.Year
        ShowingCalendar = false
        CalendarPosition = None
        AllowFuture = allowFutureDates
        FirstDayOfWeek = DayOfWeek.Sunday
        Anchor = t
        ValidationError = None
        ValidationWarning = None
        Label = label
        Placeholder = placeholder
        IsMandatory = mandatory
        Disabled = disabled
    } |> recalcDateWindow

let init id label placeholder mandatory allowFutureDates value : Model =
    initWithDisabled id label placeholder mandatory allowFutureDates value false

let monthNames =
    [|
        "Jan"
        "Feb"
        "Mar"
        "Apr"
        "May"
        "Jun"
        "Jul"
        "Aug"
        "Sep"
        "Oct"
        "Nov"
        "Dec"
    |]

let isLeapYear year =
    if year % 400 = 0 then
        true
    else if year % 100 = 0 then
        false
    else
        year % 4 = 0

let daysInMonth year index =    
    match index with
    | 9 
    | 4 
    | 6 
    | 11 -> 30
    | 2 -> 28 + (if isLeapYear year then 1 else 0)
    | _ -> 31

let dayList =
    [|
        DayOfWeek.Sunday
        DayOfWeek.Monday
        DayOfWeek.Tuesday
        DayOfWeek.Wednesday
        DayOfWeek.Thursday
        DayOfWeek.Friday
        DayOfWeek.Saturday
    |]

let inline private textInputIdToDatePickerId id = 
    sprintf "%s-datepicker-div" id

let update model msg : Model * Cmd<Msg> =
    match model.Disabled with 
    | true -> model, Cmd.none
    | false -> 
        match msg with
        | ShowCalendar inputId -> { model with ShowingCalendar = true }, Cmd.ofMsg (PositionCalendar inputId)
        | ToggleCalendar inputId -> 
            match model.ShowingCalendar with
            | false -> 
                { model with ShowingCalendar = true }, Cmd.ofMsg (PositionCalendar inputId)
            | true -> 
                { model with ShowingCalendar = false; CalendarPosition = None }, []
        | HideCalendar -> { model with ShowingCalendar = false; CalendarPosition = None }, []
        | ViewMonth i -> { model with Month = i } |> recalcDateWindow, []
        | ViewYear i -> { model with Year = i } |> recalcDateWindow, []
        | DecrementMonth ->
            if model.Month > 1 then
                { model with Month = model.Month - 1 } |> recalcDateWindow, []
            else if model.Year > 1900 then
                { model with Year = model.Year - 1; Month = 12 } |> recalcDateWindow, []
            else
                model, [] 
        | IncrementMonth ->
            if model.Month < 12 then
                { model with Month = model.Month + 1 } |> recalcDateWindow, []
            else
                { model with Year = model.Year + 1; Month = 1 } |> recalcDateWindow, []
        | DayClicked date ->
            let parsed = ParsedDate.Valid date
            Browser.Dom.console.log("Selecting date", parsed);
            { model with 
                ShowingCalendar = false
                Value = parsed
                ValidationError = None
                Month = date.Month
                Year = date.Year 
            } |> recalcDateWindow, []
        | SelectToday ->
            model, Cmd.ofMsg (DayClicked DateTime.Today)
        | PositionCalendar (TextInputId inputId) ->
            let newPosition = HTMLElementPosition.calculatePopupPosition MAX_POPUP_HEIGHT (HTMLElementPosition.HTMLElementId inputId) |> Option.map (fun x -> x.Position)
            {model with CalendarPosition = newPosition}, []

        | SetValue (date, err) -> 
            { model with Value = date; ValidationError = err; ShowingCalendar = false }, []
    
let renderMonthInList (intVal: int) =
    option [ 
        Value (intVal.ToString())
    ] [ str <| monthNames.[intVal - 1] ]

let renderYearInList (intVal: int) =
    option [ 
        Value (intVal.ToString())
        Key (intVal.ToString())
    ] [ str <| (intVal.ToString()) ]

let renderDayTile (date: DateTime) (dow: DayOfWeek) (anchor: DateTime) (selected:DateTime option) allowFuture dispatch =
    if date < anchor then
        td [
            ClassName ("ui-datepicker-other-month ui-datepicker-unselectable ui-state-disabled" + (if dow = DayOfWeek.Saturday || dow = DayOfWeek.Sunday then " ui-datepicker-week-end" else ""))
            DangerouslySetInnerHTML { __html = "&nbsp;" }
        ] []
    else
        let weekend = if dow = DayOfWeek.Saturday || dow = DayOfWeek.Sunday then "ui-datepicker-week-end" else ""
        let isCurrent = match selected with Some sel when sel = date -> true | _ -> false
        let currentContainer = if isCurrent then "ui-datepicker-current-day" else ""
        let currentValue = if isCurrent then " ui-state-active" else ""
        let today = if date = DateTime.Today then (" ui-datepicker-today", " ui-state-highlight") else ("", "")
        let futureOkay = allowFuture || date <= DateTime.Today
        let futureClass = if futureOkay then "" else " ui-datepicker-unselectable ui-state-disabled"
        td [   
            ClassName (weekend + currentContainer + fst today + futureClass)
        ] [
            a [
                ClassName ("ui-state-default" + currentValue + snd today)
                OnClick (fun _ -> if futureOkay then dispatch (DayClicked date) else ())
                Style [
                    Color (if isCurrent && date <> DateTime.Today then "white" else "inherit")
                ]
                TabIndex -1
            ] [ str <| date.Day.ToString() ]
        ]
    
let calcWeeks model =
    let start = 0 - int model.Anchor.DayOfWeek
    let e = daysInMonth model.Anchor.Year model.Anchor.Month - 1
    [start..e]
    |> List.fold (fun (i, scratch, acc) item ->
            if i % 7 = 0 then 
                (i+1, [], (item::scratch |> List.rev)::acc)
            else
                (i+1, item::scratch, acc)
        ) (1, [], [])
    |> function (_,scratch,acc) -> (scratch |> List.rev)::acc
    |> List.rev

let targetIsSelect (target: Browser.Types.EventTarget) =
    target?className = "ui-datepicker-month" || target?className = "ui-datepicker-year"

let view (model: Model) (dispatch: Msg -> unit) = 
    match model.ShowingCalendar with
    | true ->
        let now = DateTime.Today
        let lastMonth = model.AllowFuture = false && model.Year >= now.Year && model.Month >= now.Month

        div [
            Id (textInputIdToDatePickerId model.Id)
            Key (textInputIdToDatePickerId model.Id)
            ClassName "ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all"
            Style 
                [
                    yield! HTMLElementPosition.generatePositionStyle model.CalendarPosition
                    yield ZIndex "1000"
                    yield Display DisplayOptions.Block
                ]
            OnMouseDown (fun ev -> 
                if targetIsSelect ev.target then
                    ()
                else
                    ev.preventDefault()
                )
        ] [
            div [ ClassName "ui-datepicker-header ui-widget-header ui-helper-clearfix ui-corner-all" ] [
                a [ 
                    ClassName "ui-datepicker-prev ui-corner all"; 
                    Title "prev" 
                    OnClick (fun _ -> dispatch DecrementMonth)
                ] [
                    span [ ClassName "ui-icon ui-icon-circle-triangle-w" ] [ str "Prev" ]
                ]
                a [ 
                    ClassName ("ui-datepicker-next ui-corner all" + if lastMonth then " ui-state-disabled" else ""); 
                    Title "next"
                    OnClick (fun _ -> if lastMonth then () else dispatch IncrementMonth)
                ] [
                    span [ ClassName "ui-icon ui-icon-circle-triangle-e" ] [ str "Next" ]
                ]
                div [ ClassName "ui-datepicker-title" ] [
                    select [ 
                        ClassName "ui-datepicker-month" 
                        OnChange (fun ev -> dispatch (ViewMonth (Int32.Parse(!!ev.target?value))))
                        Style [ Width "45%" ]
                        TabIndex -1
                        OnBlur 
                            (fun ev ->
                                if ev.relatedTarget = null || (targetIsSelect Browser.Dom.document.activeElement = false && targetIsSelect ev.relatedTarget = false) then 
                                    dispatch HideCalendar
                            )
                        Value (model.Month.ToString())
                        
                        //related Target is always null in IE11
                        // Browser.document.activeElement evaluated as the body while switching from the year to the month select
                        OnFocus (fun _ -> ShowCalendar (TextInputId model.Id) |> dispatch)
                    ] [
                        for i in 1..12 do 
                            if model.AllowFuture || model.Year < DateTime.Today.Year || i <= DateTime.Today.Month then
                                yield renderMonthInList i
                    ]
                    select [ 
                        ClassName "ui-datepicker-year" 
                        OnChange (fun ev -> dispatch (ViewYear (Int32.Parse(!!ev.target?value))))
                        Style [ Width "45%" ]
                        TabIndex -1
                        OnBlur 
                            (fun ev ->
                                if ev.relatedTarget = null || (targetIsSelect Browser.Dom.document.activeElement = false && targetIsSelect ev.relatedTarget = false) then 
                                    dispatch HideCalendar
                            )
                        Value (model.Year.ToString())
                        
                        //related Target is always null in IE11
                        // Browser.document.activeElement evaluated as the body while switching from the month to the year select
                        OnFocus (fun _ -> ShowCalendar (TextInputId model.Id) |> dispatch)
                    ] [
                        let min = if model.Year < 1950 then 1900 else (model.Year - 50)
                        let max =
                            if model.AllowFuture then 
                                model.Year + 50
                            else
                                if model.Year + 50 >= now.Year then
                                    now.Year
                                else
                                    model.Year + 50

                        for i in min..max do 
                            yield renderYearInList i
                    ]
                ]
            ]
            table [ ClassName "ui-datepicker-calendar" ] [
                thead [] [
                    tr [] [
                        th [
                            Scope "col"
                            ClassName "ui-datepicker-week-end"
                        ]  [ span [ Title "Sunday" ] [ str "Su" ] ]
                        th [ Scope "col" ] [ span [ Title "Monday" ] [ str "Mo" ] ]
                        th [ Scope "col" ] [ span [ Title "Tuesday" ] [ str "Tu" ] ]
                        th [ Scope "col" ] [ span [ Title "Wednesday" ] [ str "We" ] ]
                        th [ Scope "col" ] [ span [ Title "Thursday" ] [ str "Th" ] ]
                        th [ Scope "col" ] [ span [ Title "Friday" ] [ str "Fr" ] ]
                        th [ 
                            Scope "col"
                            ClassName "ui-datepicker-week-end" 
                        ] [ span [ Title "Saturday" ] [ str "Sa" ] ]
                    ]
                ]
                tbody [] [
                    for weekData in calcWeeks model do
                        yield tr [] [
                            for offset in weekData do
                                let date = model.Anchor.AddDays(float offset).AddHours(1.).Date
                                yield renderDayTile (if date < model.Anchor then DateTime.MinValue else date) date.DayOfWeek model.Anchor (ParsedDate.toOption model.Value) model.AllowFuture dispatch
                        ]
                ]
            ]
            div [ ClassName "ui-datepicker-buttonpane ui-widget-content" ] [
                    button [
                        HTMLAttr.Type "button"
                        ClassName "ui-datepicker-current ui-state-default ui-priority-secondary ui-corner-all"
                        OnClick (fun _ -> dispatch SelectToday)
                        TabIndex -1
                    ] [ str "Today"]
                    button [
                        HTMLAttr.Type "button"
                        ClassName "ui-datepicker-close ui-state-default ui-priority-primary ui-corner-all"
                        OnClick (fun _ -> dispatch HideCalendar)
                        TabIndex -1
                    ] [ str "Done"]
                ]
        ]
    | false ->
        div [] []

let dateInput model (dispatch: Msg -> unit) loading extras = 
    div 
        [ 
            yield Class (sprintf "datepicker%s" (if model.Disabled then " disabled" else ""))
            if model.Disabled then yield Disabled true
        ] [
        yield input [ 
            yield Id model.Id 
            yield ClassName (jQueryUI.addSkeletonScreen "form-control hasDatepicker" None loading)
            yield HTMLAttr.Type "text"
            yield OnBlur (fun ev -> 
                //ev.relatedTarget is null in IE11
                if isBrowserIE() then 
                    if (Browser.Dom.document.activeElement = null || targetIsSelect Browser.Dom.document.activeElement = false) then
                        !!ev.target?value |> ParsedDate.ofString |> SetValue |> dispatch

                else 
                    if ev.relatedTarget = null || targetIsSelect ev.relatedTarget = false then 
                        !!ev.target?value |> ParsedDate.ofString |> SetValue |> dispatch
            ) 
            yield OnKeyDown (fun ev ->
                if ev.key = "Enter" then !!ev.target?value |> ParsedDate.ofString |> SetValue |> dispatch
            )
            yield Placeholder (FSharp.Core.Option.defaultValue "" model.Placeholder)
            yield MaxLength 20.
            yield AutoComplete "off"
            yield Helpers.valueOrDefault (model.Value |> ParsedDate.formatToOption illyDate |> FSharp.Core.Option.defaultValue "")
            yield OnFocus (fun _ -> ShowCalendar (TextInputId model.Id) |> dispatch)
            yield OnClick (fun _ -> ShowCalendar (TextInputId model.Id) |> dispatch)
            if model.Disabled then yield Disabled true
        ]
        if not loading then yield FontAwesome.solid "calendar-alt datepicker-emblem" [] []

        match model.ShowingCalendar with
        | false -> () 
        | true -> yield view model dispatch
        yield! extras
    ]

let date model dispatch loading extras =
    jQueryUI.inputGroup [
        yield jQueryUI.labelWithMandatoryAndDisabled
            model.Id 
            model.Label 
            model.ValidationError 
            model.ValidationWarning 
            loading 
            model.IsMandatory
            model.Disabled
        yield dateInput model dispatch loading extras
    ]

//utility functions
let resetValidations (model: Model) = 
    { model with ValidationError = None; ValidationWarning = None }

let setValidations (model: Model) errors warnings = 
    { model with ValidationError = errors; ValidationWarning = warnings }

let setDate (model: Model) date =
    match date with
    | None ->
        {
            model with 
                Value = ParsedDate.Empty
                ValidationError = None
                ValidationWarning = None
        }
    | Some d ->
        {
            model with 
                Value = ParsedDate.Valid d
                Month = d.Month
                Year = d.Year
                ValidationError = None
                ValidationWarning = None
        } |> recalcDateWindow

let toOption (model: Model) =
    model.Value |> ParsedDate.toOption